import {
    Box,
    Divider,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material'
import {
    AccreditationDocument,
    AccreditationDocumentDetails,
    AccreditationRequiredField,
    REG_STEP,
    TeacherFields,
} from '../../../redux/types'
import {
    secondaryIdentity,
    WORKING_RIGHTS,
} from '../RegistrationModals/components/helper'
import {
    addEmploymentSetting,
    isObjectEmpty,
} from '../../../firebase/general-apis'
import styles from '../ReviewPage/index.module.scss'
import {
    ECCAccreditationLevel,
    ECCQualToTitle,
    EducatorEmploymentLevel,
    ESQualificationOptions,
    FieldState,
    TeacherAccountState,
} from '../../../firebase/types-teacher'
import { getUserNames, isDocExpired } from '../../../helpers/utils'
import { getVerifiedAccreditationDoc, whichEmployentSetting } from '../helper'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import { TeacherRegFormControl } from '../../../components/FormControl'
import { setErrorMsg } from '../../../redux/uiSlice'
import {
    addTeacherEmploymentSetting,
    updateECCAccreditationLevel,
} from '../../../firebase/teacherApi'
import { useEffect, useMemo, useState } from 'react'
import { DashboardTabNames } from '../../../routers/type'
import pxToRem from '../../../helpers/pxToRem'
import {
    selectUserAuth,
    selectUserData,
    selectUserUid,
} from '../../../redux/userSlice'
import { logger } from '../../../helpers/logger'
import { FieldState_v2 } from '../../../schemas/teacherReg'
import { AusState } from '../../../firebase/accreditation-api'
import { UserRegSx } from '../../../components/TextField'

export interface accredDocParam {
    school: AccreditationDocumentDetails[] | null
    earlyChildcare: AccreditationDocumentDetails[] | null
    supportStaff: AccreditationDocumentDetails[] | null
}

export type ReviewGroupItem = {
    title: string
    step: string
    data: any[]
    state: string
    expired?: boolean
}

export const useReviewGroups = () => {
    const fields = useSelector((state: RootState) => state.userReg.fields)

    const user = useSelector(selectUserData)
    const auth = useSelector(selectUserAuth)

    const schoolTeachingDocuments = useSelector(
        (state: RootState) => state.userReg.stateTeachingDocuments
    )
    const eccTeachingDocuments = useSelector(
        (state: RootState) => state.userReg.stateECCDocuments
    )
    const supportStaffTeachingDocuments = useSelector(
        (state: RootState) => state.userReg.stateSupportStaffDocuments
    )
    const employmentSetting = useSelector(
        (state: RootState) => state.userReg.employmentSetting
    )
    const employmentSettings = useSelector(
        (state: RootState) => state.userReg.fields.employmentSetting
    )

    const educatorSetting = useMemo(() => {
        let setting = Object.entries(employmentSettings || {}).filter(
            (el) => el[1].type === 'earlyChildcare'
        )?.[0]

        return setting ? { ...setting[1], id: setting[0] } : undefined
    }, [employmentSettings])
    const supportStaffSetting = useMemo(() => {
        let setting = Object.entries(employmentSettings || {}).filter(
            (el) => el[1].type === 'supportStaff'
        )?.[0]
        return setting ? { ...setting[1], id: setting[0] } : undefined
    }, [employmentSettings])

    const schoolDocs: AccreditationDocumentDetails[] | null = useMemo(
        () =>
            schoolTeachingDocuments?.[`${employmentSetting?.locationState}`] ||
            null,
        [employmentSetting?.locationState, schoolTeachingDocuments]
    )
    const eccDocs: AccreditationDocumentDetails[] | null = useMemo(
        () =>
            eccTeachingDocuments?.[`${employmentSetting?.locationState}`] ||
            null,
        [employmentSetting?.locationState, eccTeachingDocuments]
    )
    const supportStaffDocs: AccreditationDocumentDetails[] | null = useMemo(
        () =>
            supportStaffTeachingDocuments?.[
                `${employmentSetting?.locationState}`
            ] || null,
        [employmentSetting?.locationState, supportStaffTeachingDocuments]
    )

    const [groups, setGroups] = useState<ReviewGroupItem[]>()

    useEffect(() => {
        try {
            // dispatch(setReviewGroups(undefined));
            setGroups(undefined)
            if (!employmentSetting) return
            const accountState = fields.accountState
                ?.value as TeacherAccountState
            const namesState = getIncompleteState({
                firstName: fields.firstName,
                lastName: fields.lastName,
            })
            const contactState = getIncompleteState({
                contact: fields.contact,
                homeAddress: fields.homeAddress,
            })
            const basicDetailsState = getCombinedState([
                namesState,
                contactState,
            ])

            const profilePhotoState = getIncompleteState({
                profilePicture: fields.profilePicture,
            })
            const workingRightsState = getIncompleteState({
                workingRights: fields.workingRights,
            })

            const primaryIdentityState = getIncompleteState({
                primaryIdentification: fields.primaryIdentification,
            })
            const secondaryIdentityState = getIncompleteState({
                secondaryIdentification: fields.secondaryIdentification,
            })
            const holdingIDState = getIncompleteState({
                holdingIdPhotoURL: fields.holdingIdPhotoURL,
            })

            const birthDateError =
                fields.primaryIdentification?.birthDate &&
                fields.secondaryIdentification?.birthDate
                    ? fields.primaryIdentification.birthDate ===
                      fields.secondaryIdentification.birthDate
                    : true
            const identityState = getCombinedState([
                primaryIdentityState,
                secondaryIdentityState,
                holdingIDState,
                ...(!birthDateError ? [FieldState.INCOMPLETE] : []),
            ])
            const locationState = employmentSetting.locationState

            let schoolTeachingDocs: {
                [type: string]: AccreditationDocument | undefined
            } = {}
            let schoolTeachingDocsStates: FieldState[] = []
            if (
                schoolDocs &&
                ['school', 'both'].includes(employmentSetting.type)
            ) {
                for (let i of schoolDocs || []) {
                    schoolTeachingDocs = {
                        ...schoolTeachingDocs,
                        [`${i.type}`]:
                            fields.accreditation?.['school']?.[
                                `${locationState}`
                            ]?.[`${i.type}`],
                    }
                    if (!i.optional) {
                        schoolTeachingDocsStates = [
                            ...schoolTeachingDocsStates,
                            (fields.accreditation?.['school']?.[
                                `${locationState}`
                            ]?.[`${i.type}`]?.state ||
                                FieldState.INCOMPLETE) as FieldState,
                        ]
                    }
                }
            }
            let eccTeachingDocs: {
                [type: string]: AccreditationDocument | undefined
            } = {}
            let eccTeachingDocsStates: FieldState[] = []
            if (['earlyChildcare', 'both'].includes(employmentSetting.type)) {
                for (let i of eccDocs || []) {
                    eccTeachingDocs = {
                        ...eccTeachingDocs,
                        [`${i.type}`]:
                            fields.accreditation?.['earlyChildcare']?.[
                                `${fields.setLocation?.homeState}`
                            ]?.[`${i.type}`],
                    }
                    if (!i.optional) {
                        eccTeachingDocsStates = [
                            ...eccTeachingDocsStates,
                            (fields.accreditation?.['earlyChildcare']?.[
                                `${fields.setLocation?.homeState}`
                            ]?.[`${i.type}`]?.state ||
                                FieldState.INCOMPLETE) as FieldState,
                        ]
                    }
                }
            }

            let supportStaffTeachingDocs: {
                [type: string]: AccreditationDocument | undefined
            } = {}
            let supportStaffTeachingDocsStates: FieldState[] = []
            if (employmentSetting.type === 'supportStaff') {
                for (let i of supportStaffDocs || []) {
                    supportStaffTeachingDocs = {
                        ...supportStaffTeachingDocs,
                        [`${i.type}`]:
                            fields.accreditation?.['supportStaff']?.[
                                `${fields.setLocation?.homeState}`
                            ]?.[`${i.type}`],
                    }
                    if (!i.optional) {
                        supportStaffTeachingDocsStates = [
                            ...supportStaffTeachingDocsStates,
                            (fields.accreditation?.['supportStaff']?.[
                                `${fields.setLocation?.homeState}`
                            ]?.[`${i.type}`]?.state ||
                                FieldState.INCOMPLETE) as FieldState,
                        ]
                    }
                }
            }

            // const schoolTeachingDocsState = schoolTeachingDocsStates.length === 0 ? FieldState.SUBMITTED :
            // const schoolTeachingDocsState = Object.values(schoolTeachingDocs).length === 0 ? FieldState.SUBMITTED : getCombinedState(schoolTeachingDocsStates);
            // // const eccTeachingDocsState = Object.values(eccTeachingDocs).length === 0 ? FieldState.SUBMITTED : getCombinedState(Object.values(eccTeachingDocs).map(el => el?.state || FieldState.INCOMPLETE))
            // const eccTeachingDocsState = Object.values(eccTeachingDocsStates).length === 0 ? FieldState.SUBMITTED : getCombinedState(eccTeachingDocsStates);

            const referenceState = getIncompleteState({
                reference: fields.reference,
            })
            const emergencyState = getIncompleteState({
                firstEmergencyContact: fields.firstEmergencyContact,
                secondEmergencyContact: fields.secondEmergencyContact,
            })
            const primaryContactState = getIncompleteState({
                firstEmergencyContact: fields.firstEmergencyContact,
            })
            const secondaryContactState = getIncompleteState({
                secondEmergencyContact: fields.secondEmergencyContact,
            })

            const bankState = getIncompleteState({ bank: fields.bank })
            const superState = getIncompleteState({ super: fields.super })
            const taxState = getIncompleteState({ tax: fields.tax })

            const accredLevelState =
                fields.service && Object.keys(fields.service).length > 0
                    ? FieldState.SUBMITTED
                    : FieldState.INCOMPLETE

            const {
                firstName: userFn,
                lastName: userLn,
                preferredName: userPn,
            } = getUserNames(
                user.data?.firstNameActual,
                user.data?.lastName,
                user.data?.firstName
            )

            let firstName = fields.firstName?.value || userFn
            let lastName = fields.lastName?.value || userLn
            let preferredName = fields.preferredName?.value || userPn

            const temp = [
                {
                    title: 'Basic Details',
                    step: REG_STEP.NAMES,
                    data: [
                        {
                            field: 'Names',
                            state: namesState as FieldState,
                            step: REG_STEP.NAMES,
                        },
                        { field: 'First Name', value: firstName },
                        { field: 'Last Name', value: lastName },
                        {
                            field: 'Preferred Name',
                            value: preferredName || '-',
                        },
                        {
                            field: 'Contact Information',
                            state: contactState as FieldState,
                            step: REG_STEP.CONTACT,
                        },
                        {
                            field: 'Email',
                            value:
                                fields.email?.value || user.data?.email || '',
                        },
                        {
                            field: 'Contact Number',
                            value: fields.contact?.value,
                        },
                        {
                            field: 'Address',
                            value: fields.homeAddress?.address,
                        },
                    ],
                    state: basicDetailsState,
                },
                {
                    title: 'Profile Photo',
                    step: REG_STEP.PROFILE_PHOTO,
                    data: [
                        {
                            field: 'Profile Photo',
                            state: profilePhotoState as FieldState,
                            step: REG_STEP.PROFILE_PHOTO,
                        },
                        {
                            field: '',
                            value:
                                fields.profilePicture &&
                                fields.profilePicture.value ? (
                                    <Box className={styles.imgContainer}>
                                        {' '}
                                        <img
                                            src={fields.profilePicture?.value}
                                            alt={'Uploaded user profile'}
                                        />{' '}
                                    </Box>
                                ) : (
                                    <p style={{ textAlign: 'right' }}>
                                        Not uploaded
                                    </p>
                                ),
                        },
                    ],
                    state: profilePhotoState,
                },
                {
                    title: 'Working Rights',
                    step: REG_STEP.WORKING_RIGHTS,
                    data: [
                        {
                            field: 'Document',
                            state: workingRightsState as FieldState,
                            step: REG_STEP.WORKING_RIGHTS,
                        },
                        {
                            field: 'Working Rights',
                            value: renameRights(fields.workingRights?.value),
                        },
                        ...(wrDocs(fields.workingRights?.value)
                            ? [
                                  //   {
                                  //       field: 'Visa Grant Date',
                                  //       value: fields.workingRights?.grant,
                                  //   },
                                  //   {
                                  //       field: 'Visa Expiry Date',
                                  //       value: fields.workingRights?.expiry,
                                  //   },
                                  //   {
                                  //       field: 'Visa Class/Subclass',
                                  //       value: fields.workingRights?.visaClass,
                                  //   },
                                  //   {
                                  //       field: 'Working Entitlements/Visa Conditions',
                                  //       value:
                                  //           fields.workingRights
                                  //               ?.workingEntitlements !==
                                  //           undefined
                                  //               ? fields.workingRights
                                  //                     .workingEntitlements
                                  //                   ? 'YES'
                                  //                   : 'NO'
                                  //               : undefined,
                                  //   },
                                  {
                                      field: 'Document Uploaded YES/NO',
                                      value: fields.workingRights?.url
                                          ? 'YES'
                                          : 'NO',
                                  },
                              ]
                            : []),
                    ],
                    state: workingRightsState,
                },
                {
                    title: 'Identity Verification',
                    step: REG_STEP.PRIMARY_DOC,
                    data: [
                        {
                            field: 'Primary Identification',
                            state: primaryIdentityState as FieldState,
                            step: REG_STEP.PRIMARY_DOC,
                        },
                        {
                            field: 'Primary Identification',
                            value: renameDoc(
                                fields.primaryIdentification?.documentType
                            ),
                        },
                        {
                            field: 'Country of Issue',
                            value: fields.primaryIdentification?.country,
                        },
                        {
                            field: 'Name on Document',
                            value: fields.primaryIdentification?.name,
                        },
                        {
                            field: 'Date of Birth',
                            value: (
                                <span
                                    style={{
                                        color: !birthDateError
                                            ? styles.destructiveRed500
                                            : styles.shades100,
                                    }}
                                >
                                    {fields.primaryIdentification?.birthDate}
                                </span>
                            ),
                        },
                        {
                            field: 'Document Number',
                            value: fields.primaryIdentification?.reference,
                        },
                        {
                            field: 'Document Uploaded YES/NO',
                            value: fields.primaryIdentification?.value
                                ? 'YES'
                                : 'NO',
                        },

                        {
                            field: 'Secondary Identification',
                            state: secondaryIdentityState as FieldState,
                            step: REG_STEP.SECONDARY_DOC,
                        },
                        {
                            field: 'Secondary Identification',
                            value: renameDoc(
                                fields.secondaryIdentification?.documentType
                            ),
                        },
                        {
                            field: 'Country of Issue',
                            value: fields.secondaryIdentification?.country,
                        },
                        {
                            field: 'Name on Document',
                            value: fields.secondaryIdentification?.name,
                        },
                        {
                            field: 'Date of Birth',
                            value: (
                                <span
                                    style={{
                                        color: !birthDateError
                                            ? styles.destructiveRed500
                                            : styles.shades100,
                                    }}
                                >
                                    {fields.secondaryIdentification?.birthDate}
                                </span>
                            ),
                        },
                        {
                            field: 'Document Number',
                            value: fields.secondaryIdentification?.reference,
                        },
                        {
                            field: 'Document Uploaded YES/NO',
                            value: fields.secondaryIdentification?.value
                                ? 'YES'
                                : 'NO',
                        },

                        {
                            field: 'Photo Holding ID',
                            state: holdingIDState as FieldState,
                            step: REG_STEP.HOLDING_ID,
                        },
                        {
                            field: 'Document Uploaded YES/NO',
                            value: fields.holdingIdPhotoURL?.value
                                ? 'YES'
                                : 'NO',
                        },
                    ],
                    state: identityState,
                },
                ['school', 'both'].includes(employmentSetting.type) &&
                schoolDocs !== undefined
                    ? {
                          title: 'Teaching Accreditation',
                          step:
                              schoolDocs && schoolDocs.length > 0
                                  ? schoolDocs[0].type
                                  : REG_STEP.TEACHING_DOCS,
                          data: [
                              // ...(schoolDocs.length === 0 ?
                              ...(schoolDocs === null
                                  ? [
                                        {
                                            field: 'We currently do not operate in this state',
                                        },
                                    ]
                                  : schoolDocs.length === 0
                                    ? [
                                          {
                                              field: 'No documents are required for this state',
                                          },
                                      ]
                                    : schoolDocs.flatMap((el) => {
                                          let accred =
                                              getVerifiedAccreditationDoc(
                                                  fields.accreditation
                                                      ?.school?.[
                                                      `${locationState}`
                                                  ],
                                                  el.type
                                              )
                                          // let accred = fields.accreditation?.school?.[`${locationState}`]?.[`${el.type}`];
                                          return [
                                              {
                                                  field: el.optional
                                                      ? `(Optional) ${el.short_title}`
                                                      : el.short_title,
                                                  state:
                                                      accred?.state ||
                                                      FieldState.INCOMPLETE,
                                                  step: el.type,
                                                  expired: isDocExpired(
                                                      accred?.expiryDate
                                                  ),
                                              },
                                              ...getAccredDocFields(
                                                  accred,
                                                  el.required_datefield
                                              ),
                                              ...(el.no_reference
                                                  ? []
                                                  : [
                                                        {
                                                            field: 'Document Reference',
                                                            value: accred?.reference,
                                                        },
                                                    ]),
                                              {
                                                  field: 'Document Uploaded YES/NO',
                                                  value: accred?.imageURL
                                                      ? 'YES'
                                                      : 'NO',
                                              },
                                          ]
                                      })),
                              ...(employmentSetting.type === 'school' &&
                              !location.pathname.includes(
                                  DashboardTabNames.REVIEW
                              )
                                  ? [
                                        {
                                            field: '',
                                            value: (
                                                <Stack
                                                    role="button"
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() =>
                                                        auth.status?.uid &&
                                                        addEmploymentSetting(
                                                            auth.status.uid,
                                                            employmentSetting.locationState,
                                                            'earlyChildcare'
                                                        )
                                                    }
                                                    flex={1}
                                                    direction={'row'}
                                                    justifyContent={'center'}
                                                    alignItems={'center'}
                                                    gap={pxToRem(12)}
                                                    pt={pxToRem(12)}
                                                >
                                                    <Divider
                                                        flexItem
                                                        sx={{ width: '100%' }}
                                                    >
                                                        <Typography>
                                                            Add Educator
                                                            Application
                                                        </Typography>
                                                    </Divider>
                                                    {/* <Box
                                                        sx={{
                                                            height: pxToRem(36),
                                                            width: pxToRem(36),
                                                            backgroundColor:
                                                                styles.primaryYellow500,
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                            display: 'flex',
                                                            borderRadius:
                                                                pxToRem(8),
                                                        }}
                                                    > */}
                                                    {/* prettier-ignore */}
                                                    {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.99999 4.16669V15.8334M4.16666 10H15.8333" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> </svg> */}
                                                    {/* </Box> */}
                                                </Stack>
                                            ),
                                        },
                                    ]
                                  : []),
                          ],
                          state: fields.accreditation?.school?.[
                              `${locationState}`
                          ]?.state,
                          expired:
                              schoolDocs?.length === 0
                                  ? undefined
                                  : fields.accreditation?.school?.[
                                        `${locationState}`
                                    ]?.state === FieldState.EXPIRED,
                          // schoolDocs?.filter(el => !el.optional).some(el => isExpired(fields.accreditation?.school?.[`${locationState}`]?.[`${el.type}`], el.required_datefield, el.renewal_frequency_years) )
                      }
                    : null,
                ['earlyChildcare', 'both'].includes(employmentSetting.type) &&
                eccDocs !== undefined
                    ? {
                          title: 'Early Childhood Accreditation',
                          step:
                              eccDocs && eccDocs.length > 0
                                  ? eccDocs[0].type
                                  : REG_STEP.TEACHING_DOCS,
                          data: [
                              //   ...(['VIC', 'NSW'].includes(locationState)
                              //       ? [
                              //             {
                              //                 field: 'My Early Childhood Accreditation Level is:',
                              //                 value: (
                              //                     <ECCAccreditationLevelSelect
                              //                         settingId={employmentSetting}
                              //                         setting={}
                              //                         disabled={
                              //                             location.pathname.includes(
                              //                                 DashboardTabNames.REVIEW
                              //                             ) ||
                              //                             (accountState ===
                              //                                 TeacherAccountState.SUBMITTED &&
                              //                                 educatorSetting?.state !==
                              //                                     TeacherAccountState.REJECTED)
                              //                         }
                              //                     />
                              //                 ),
                              //             },
                              //         ]
                              //       : []),
                              // ...(docs.ecc.length === 0 ?
                              ...(eccDocs === null
                                  ? [
                                        {
                                            field: 'We currently do not operate in this state',
                                        },
                                    ]
                                  : !['VIC', 'NSW'].includes(locationState) &&
                                      eccDocs.length === 0
                                    ? [
                                          {
                                              field: 'No documents are required for this state',
                                          },
                                      ]
                                    : [
                                          // ...getECCDocsByLevel(eccDocs, employmentSetting).flatMap(el => {
                                          ...eccDocs.flatMap((el) => {
                                              let accred =
                                                  getVerifiedAccreditationDoc(
                                                      fields.accreditation
                                                          ?.earlyChildcare?.[
                                                          `${locationState}`
                                                      ],
                                                      el.type
                                                  )
                                              // let accred = fields.accreditation?.earlyChildcare?.[`${locationState}`]?.[`${el.type}`]
                                              return [
                                                  {
                                                      // field: el.short_title,
                                                      field: el.optional
                                                          ? `(Optional) ${el.short_title}`
                                                          : el.short_title,
                                                      state:
                                                          accred?.state ||
                                                          FieldState.INCOMPLETE,
                                                      step: el.type,
                                                      expired: isDocExpired(
                                                          accred?.expiryDate
                                                      ),
                                                  },
                                                  ...getAccredDocFields(
                                                      accred,
                                                      el.required_datefield
                                                  ),
                                                  ...(el.type ===
                                                  'earlyChildcareQualification'
                                                      ? [
                                                            {
                                                                field: 'Qualification',
                                                                value: ECCQualToTitle(
                                                                    accred?.qualification
                                                                ),
                                                            },
                                                        ]
                                                      : []),
                                                  ...(el.no_reference
                                                      ? []
                                                      : [
                                                            {
                                                                field: 'Document Reference',
                                                                value: accred?.reference,
                                                            },
                                                        ]),
                                                  {
                                                      field: 'Document Uploaded YES/NO',
                                                      value: accred?.imageURL
                                                          ? 'YES'
                                                          : 'NO',
                                                  },
                                              ]
                                          }),
                                      ]),
                              ...(employmentSetting.type === 'earlyChildcare' &&
                              !location.pathname.includes(
                                  DashboardTabNames.REVIEW
                              )
                                  ? [
                                        {
                                            field: '',
                                            value: (
                                                <Stack
                                                    role="button"
                                                    sx={{
                                                        cursor: 'pointer',
                                                        flex: 1,
                                                    }}
                                                    onClick={() =>
                                                        auth.status?.uid &&
                                                        addEmploymentSetting(
                                                            auth.status.uid,
                                                            employmentSetting.locationState,
                                                            'school'
                                                        )
                                                    }
                                                    flex={1}
                                                    direction={'row'}
                                                    justifyContent={'center'}
                                                    alignItems={'center'}
                                                    gap={pxToRem(12)}
                                                    pt={pxToRem(12)}
                                                >
                                                    <Divider
                                                        flexItem
                                                        sx={{ width: '100%' }}
                                                    >
                                                        <Typography>
                                                            Add Teacher
                                                            Application
                                                        </Typography>
                                                    </Divider>
                                                    {/* <Box
                                                        sx={{
                                                            height: pxToRem(36),
                                                            width: pxToRem(36),
                                                            backgroundColor:
                                                                styles.primaryYellow500,
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'center',
                                                            display: 'flex',
                                                            borderRadius:
                                                                pxToRem(8),
                                                        }}
                                                    > */}
                                                    {/* prettier-ignore */}
                                                    {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.99999 4.16669V15.8334M4.16666 10H15.8333" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/> </svg> */}
                                                    {/* </Box> */}
                                                </Stack>
                                            ),
                                        },
                                    ]
                                  : []),
                          ],
                          state: fields.accreditation?.earlyChildcare?.[
                              `${locationState}`
                          ]?.state,
                          expired:
                              eccDocs?.length === 0
                                  ? undefined
                                  : fields.accreditation?.earlyChildcare?.[
                                        `${locationState}`
                                    ]?.state === FieldState.EXPIRED,
                          // eccDocs?.filter(el => !el.optional).some(el => isExpired(fields.accreditation?.earlyChildcare?.[`${locationState}`]?.[`${el.type}`], el.required_datefield, el.renewal_frequency_years) )
                      }
                    : null,
                employmentSetting.type === 'supportStaff' &&
                supportStaffDocs !== undefined
                    ? {
                          title: 'Education Support Staff Accreditation',
                          step:
                              supportStaffDocs && supportStaffDocs.length > 0
                                  ? supportStaffDocs[0].type
                                  : REG_STEP.TEACHING_DOCS,
                          data: [
                              //   {
                              //       field: 'Have you completed any relevant tertiary or other formal qualifications in education? (e.g., certificate, diploma, university degree.)*',
                              //       value: (
                              //           <ESQualificationSelector
                              //               employmentSetting={
                              //                   supportStaffSetting
                              //               }
                              //           />
                              //       ),
                              //   },
                              ...(supportStaffDocs === null
                                  ? [
                                        {
                                            field: 'We currently do not operate in this state',
                                        },
                                    ]
                                  : supportStaffDocs.length === 0
                                    ? [
                                          {
                                              field: 'No documents are required for this state',
                                          },
                                      ]
                                    : supportStaffDocs.flatMap((el) => {
                                          let accred =
                                              getVerifiedAccreditationDoc(
                                                  fields.accreditation
                                                      ?.supportStaff?.[
                                                      `${locationState}`
                                                  ],
                                                  el.type
                                              )
                                          // let accred = fields.accreditation?.school?.[`${locationState}`]?.[`${el.type}`];
                                          return [
                                              {
                                                  field: el.optional
                                                      ? `(Optional) ${el.short_title}`
                                                      : el.short_title,
                                                  state:
                                                      accred?.state ||
                                                      FieldState.INCOMPLETE,
                                                  step: el.type,
                                                  expired: isDocExpired(
                                                      accred?.expiryDate
                                                  ),
                                              },
                                              ...getAccredDocFields(
                                                  accred,
                                                  el.required_datefield
                                              ),
                                              ...(el.type ===
                                              'supportStaffQualification'
                                                  ? [
                                                        {
                                                            field: 'Qualification',
                                                            value:
                                                                ESQualificationOptions.find(
                                                                    (el) =>
                                                                        el.value ===
                                                                        accred?.qualification
                                                                )?.label || '-',
                                                        },
                                                    ]
                                                  : []),
                                              ...(el.no_reference
                                                  ? []
                                                  : [
                                                        {
                                                            field: 'Document Reference',
                                                            value: accred?.reference,
                                                        },
                                                    ]),
                                              {
                                                  field: 'Document Uploaded YES/NO',
                                                  value: accred?.imageURL
                                                      ? 'YES'
                                                      : 'NO',
                                              },
                                          ]
                                      })),
                          ],
                          state: fields.accreditation?.supportStaff?.[
                              `${locationState}`
                          ]?.state,
                          expired:
                              supportStaffDocs?.length === 0
                                  ? undefined
                                  : fields.accreditation?.supportStaff?.[
                                        `${locationState}`
                                    ]?.state === FieldState.EXPIRED,
                          // schoolDocs?.filter(el => !el.optional).some(el => isExpired(fields.accreditation?.school?.[`${locationState}`]?.[`${el.type}`], el.required_datefield, el.renewal_frequency_years) )
                      }
                    : null,
                {
                    title: 'Reference',
                    step: REG_STEP.REFERENCE,
                    data: [
                        {
                            field: 'Reference',
                            state: referenceState as FieldState,
                            step: REG_STEP.REFERENCE,
                        },
                        ...(fields.reference?.requestedInterview
                            ? [{ field: 'Requested for Online Interview', value: ' ' }]
                            : [
                                  {
                                      field: 'First Name',
                                      value: fields.reference?.firstName,
                                  },
                                  {
                                      field: 'Last Name',
                                      value: fields.reference?.lastName,
                                  },
                                  {
                                      field: 'Email',
                                      value: fields.reference?.email,
                                  },
                              ]),
                    ],
                    state: referenceState,
                },
                {
                    title: 'Emergency Contacts',
                    step: REG_STEP.EMERGENCY_CONTACTS,
                    data: [
                        {
                            field: 'Primary Contact',
                            state: primaryContactState,
                            step: REG_STEP.EMERGENCY_CONTACTS,
                        },
                        {
                            field: 'Name',
                            value: fields.firstEmergencyContact?.name,
                        },
                        {
                            field: 'Relationship',
                            value: fields.firstEmergencyContact?.relationship,
                        },
                        {
                            field: 'Contact',
                            value: fields.firstEmergencyContact?.contact,
                        },
                        {
                            field: 'Secondary Contact',
                            state: secondaryContactState,
                            step: REG_STEP.EMERGENCY_CONTACTS,
                        },
                        {
                            field: 'Name',
                            value: fields.secondEmergencyContact?.name,
                        },
                        {
                            field: 'Relationship',
                            value: fields.secondEmergencyContact?.relationship,
                        },
                        {
                            field: 'Contact',
                            value: fields.secondEmergencyContact?.contact,
                        },
                    ],
                    state: emergencyState,
                },
                locationState === 'NSW' &&
                ['school', 'both'].includes(employmentSetting.type) &&
                ![
                    TeacherAccountState.APPROVED,
                    TeacherAccountState.ONHOLD,
                ].includes(fields.accountState?.value as TeacherAccountState)
                    ? {
                          title: 'Accreditation Level',
                          step: REG_STEP.ACCREDITATION_LEVEL,
                          data: [
                              {
                                  field: 'Casual Teacher Band',
                                  state: accredLevelState,
                                  step: REG_STEP.ACCREDITATION_LEVEL,
                              },
                              {
                                  field: 'Statement of Service Uploaded (YES/NO)',
                                  value:
                                      fields.service &&
                                      !isObjectEmpty(fields.service)
                                          ? 'YES'
                                          : 'NO',
                              },
                          ],
                          state: accredLevelState,
                      }
                    : null,
                {
                    title: 'Bank Details',
                    step: REG_STEP.BANK,
                    data: [
                        {
                            field: 'Bank Details',
                            state: bankState as FieldState,
                            step: REG_STEP.BANK,
                        },
                        {
                            field: 'Account Name',
                            value: fields.bank?.accountName,
                        },
                        { field: 'BSB Number', value: fields.bank?.bsb },
                        {
                            field: 'Account Number',
                            value: fields.bank?.accountNumber,
                        },
                        ...(fields.bank?.value
                            ? [
                                  {
                                      field: 'Document Uploaded YES/NO',
                                      value: fields.bank?.value ? 'YES' : 'NO',
                                  },
                              ]
                            : []),
                    ],
                    state: bankState,
                },
                {
                    title: 'Superannuation',
                    step: REG_STEP.SUPER,
                    data: [
                        {
                            field: 'Superannuation',
                            state: superState as FieldState,
                            step: REG_STEP.SUPER,
                        },
                        {
                            field: 'Document Uploaded YES/NO',
                            value: !!fields.super?.current?.docUrl
                                ? 'YES'
                                : 'NO',
                        },
                    ],
                    state: superState,
                },
                {
                    title: 'Tax File Number Declaration',
                    step: REG_STEP.TAX,
                    data: [
                        {
                            field: 'Tax File Number Declaration',
                            state: taxState as FieldState,
                            step: REG_STEP.TAX,
                        },
                        {
                            field: 'Document Uploaded YES/NO',
                            value: fields.tax?.current?.docUrl ? 'YES' : 'NO',
                        },
                    ],
                    state: taxState,
                },
            ].filter((group) => group !== null)

            // dispatch(setReviewGroups((temp as ReviewGroupItem[])));
            setGroups(temp as ReviewGroupItem[])
        } catch (e) {
            logger.error(e)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employmentSetting, fields, schoolDocs, eccDocs, user])

    return groups
}

interface IECCAccreditationLevelSelect {
    // setting: EmploymentSetting
    settingId: string | undefined
    level: EducatorEmploymentLevel | undefined
    locationState: AusState
    label?: boolean
    placeholder?: string
    disabled?: boolean
}
export const ECCAccreditationLevelSelect = ({
    settingId,
    level,
    locationState,
    label,
    disabled,
}: IECCAccreditationLevelSelect) => {
    const userId = useSelector(selectUserUid)
    const dispatch = useDispatch()

    const handleChange = async (level: ECCAccreditationLevel) => {
        try {
            if (!userId) throw new Error('User not authenticated.')

            if (typeof settingId === 'string') {
                const res = await updateECCAccreditationLevel(
                    userId,
                    settingId,
                    level
                )
                if (res.code !== 200) throw res.error
            } else {
                // setting doesn't exist, add new employment setting
                const res = await addTeacherEmploymentSetting(
                    userId,
                    'earlyChildcare',
                    locationState,
                    level
                )
                if (res.code !== 200) throw res.error
            }
        } catch (error) {
            dispatch(
                setErrorMsg(
                    error instanceof Error
                        ? error.message
                        : JSON.stringify(error)
                )
            )
        }
    }

    return (
        <>
            <TeacherRegFormControl
                variant="outlined"
                sx={{
                    ...UserRegSx,
                    ...(label ? { width: '500px', maxWidth: '100%' } : {}),
                }}
                disabled={disabled}
            >
                {label ? (
                    <InputLabel>
                        My Early Childhood Accreditation Level is:
                    </InputLabel>
                ) : null}
                <Select
                    label={
                        label
                            ? 'My Early Childhood Accreditation Level is:'
                            : null
                    }
                    value={level || ''}
                    onChange={(e) =>
                        handleChange(e.target.value as ECCAccreditationLevel)
                    }
                >
                    <MenuItem disabled value="">
                        <em>Select your level here...</em>
                    </MenuItem>
                    {/* <MenuItem value={'educator'}>Educator</MenuItem> */}
                    <MenuItem value={'certIII'}>Cert III</MenuItem>
                    <MenuItem value={'diploma'}>Diploma</MenuItem>
                    <MenuItem value={'earlyChildhoodTrained'}>ECT</MenuItem>
                </Select>
            </TeacherRegFormControl>
        </>
    )
}

export const renameRights = (rights?: string) => {
    if (!rights) {
        return '-'
    }
    return WORKING_RIGHTS.filter((el) => el.value === rights)[0].label
}

const wrDocs = (rights?: string) => rights === 'temporaryVisaHolder'

const renameDoc = (doc?: string) => {
    if (!doc) {
        return '-'
    }
    const docMatch = secondaryIdentity.filter((el) => el.value === doc)[0]
    return docMatch ? docMatch.label : '-'
    // return secondaryIdentity.filter(el => el.value === doc )[0].label;
}

export const getAccountDetailStatus = (
    teacher: TeacherFields
): TeacherAccountState => {
    const locationState =
        teacher.setLocation?.homeState ||
        teacher.homeAddress?.homeState ||
        'VIC'
    const empType = whichEmployentSetting(
        locationState,
        teacher.employmentSetting
    )
    let states = [
        teacher.firstName?.state || 'incomplete',
        teacher.lastName?.state || 'incomplete',
        teacher.contact?.state || 'incomplete',
        teacher.homeAddress?.state || 'incomplete',
        teacher.profilePicture?.state || 'incomplete',
        teacher.workingRights?.state || 'incomplete',
        teacher.primaryIdentification?.state || 'incomplete',
        teacher.secondaryIdentification?.state || 'incomplete',
        teacher.holdingIdPhotoURL?.state || 'incomplete',
        teacher.reference?.state || 'incomplete',
        teacher.firstEmergencyContact?.state || 'incomplete',
        teacher.secondEmergencyContact?.state || 'incomplete',

        // Accreditation states
        ...(['school', 'both'].includes(empType.type)
            ? [
                  teacher.accreditation?.school?.[`${locationState}`]?.state ||
                      FieldState.INCOMPLETE,
              ]
            : []),
        ...(['earlyChildcare', 'both'].includes(empType.type)
            ? [
                  teacher.accreditation?.earlyChildcare?.[`${locationState}`]
                      ?.state || FieldState.INCOMPLETE,
              ]
            : []),
        ...(empType.type === 'supportStaff'
            ? [
                  teacher.accreditation?.supportStaff?.[`${locationState}`]
                      ?.state || FieldState.INCOMPLETE,
              ]
            : []),
        // ...Object.values(teacher.employmentSetting || {}).map(el => el.state || FieldState.INCOMPLETE)
    ]

    if (
        states.some((el) =>
            [FieldState.REJECTED, FieldState.EXPIRED].includes(el as FieldState)
        )
    )
        return TeacherAccountState.REJECTED
    if (states.some((el) => (el as FieldState) === FieldState.INCOMPLETE))
        return TeacherAccountState.INCOMPLETE
    if (states.some((el) => (el as FieldState) === FieldState.SUBMITTED))
        return TeacherAccountState.SUBMITTED
    if (states.every((el) => FieldState.APPROVED === (el as FieldState)))
        return TeacherAccountState.APPROVED

    return TeacherAccountState.INCOMPLETE

    // if (states.every(el => el as FieldState === FieldState.APPROVED)) { return AccountItemStatus.APPROVED; }
    // if (states.every(el => el as FieldState === FieldState.SUBMITTED)) { return AccountItemStatus.SUBMITTED; }
    // if (states.some(el => [FieldState.APPROVED, FieldState.SUBMITTED].includes(el as FieldState))) { return AccountItemStatus.PARTIALLY_COMPLETE }
    // return AccountItemStatus.INCOMPLETE;
}

export const getIncompleteState = (fields: TeacherFields) => {
    const match = Object.entries(fields).filter((el) => {
        if (el[0] === 'employmentDeclarations') {
            return null
        }
        if (el[1] && el[1].state && el[1].state === 'rejected') {
            return true
        }
        return false
    })[0]
    if (match) {
        return 'rejected'
    }

    // At this point it is not rejected
    // if there is any incomplete, then it is incomplete
    // Then if there are any submitted, then submitted,
    // Finally it should be approved

    const isIncomplete = Object.entries(fields).filter(([key, value]) => {
        if (
            key === 'employmentDeclarations' ||
            key === 'preferredName' ||
            key === 'bank' ||
            key === 'super' ||
            key === 'tax'
        ) {
            return null
        }
        if (value && value.state && value.state === 'incomplete') {
            // console.log(`${key}: ${JSON.stringify(value)} is incomplete`)
            return true
        }
        if (key === 'service' && isObjectEmpty(value)) {
            return true
        }
        return false
    })[0]

    // Account is incomplete if the birthdates do not match
    const birthDateSame =
        fields.primaryIdentification?.birthDate &&
        fields.secondaryIdentification?.birthDate
            ? fields.primaryIdentification.birthDate ===
              fields.secondaryIdentification.birthDate
            : true
    if (isIncomplete || !birthDateSame) {
        return 'incomplete'
    }

    // If there are any fields that are missing,

    const isSubmitted = Object.entries(fields).filter(([key, value]) => {
        if (key === 'employmentDeclarations' || key === 'preferredName') {
            return null
        }
        if (value && value.state && value.state === 'submitted') {
            return true
        }
        return false
    })[0]
    if (isSubmitted) {
        return 'submitted'
    }

    const isApproved = Object.entries(fields).filter(([key, value]) => {
        if (key === 'employmentDeclarations' || key === 'preferredName') {
            return null
        }
        if (value && value.state && value.state === 'approved') {
            return true
        }
        return false
    })[0]
    if (isApproved) {
        return 'approved'
    }

    return 'incomplete'
}

export const getCombinedState = (states: (string | FieldState)[]) => {
    if (states.includes(FieldState.REJECTED.valueOf())) {
        return FieldState.REJECTED
    } else if (states.includes(FieldState.EXPIRED.valueOf())) {
        return FieldState.EXPIRED
    } else if (states.includes(FieldState.INCOMPLETE.valueOf())) {
        return FieldState.INCOMPLETE
    } else if (states.includes(FieldState.EXPIRED.valueOf())) {
        return FieldState.EXPIRED
    } else if (states.includes(FieldState.SUBMITTED.valueOf())) {
        return FieldState.SUBMITTED
    } else if (states.includes(FieldState.APPROVED.valueOf())) {
        return FieldState.APPROVED
    } else {
        return FieldState.INCOMPLETE
    }
}

// Check dervies the overruling state given a list of states
export const getCombinedState_v2 = (states: FieldState_v2[]): FieldState_v2 => {
    if (states.length === 0) return 'incomplete'

    if (states.some((el) => el === 'rejected')) return 'rejected'
    if (states.some((el) => el === 'expired')) return 'expired'
    if (states.some((el) => el === 'incomplete')) return 'incomplete'

    if (states.some((el) => el === 'submitted')) return 'submitted'

    return 'approved'
}

const getAccredDocFields = (
    accred: AccreditationDocument | undefined,
    dateField: AccreditationRequiredField | undefined
) => {
    switch (dateField) {
        case 'registrationDate':
            return [
                {
                    field: 'Date Completed',
                    value: accred ? accred?.registrationDate : undefined,
                },
            ]
        case null:
            return []
        case 'expiryDate':
        default:
            return [
                {
                    field: 'Expiry Date',
                    value: accred ? accred.expiryDate : undefined,
                },
            ]
    }
}

// const isExpired = (accred: AccreditationDocument | undefined, required_date: AccreditationRequiredField | undefined, renewalYears: number | undefined) => {
//     if (accred === undefined) { return true }
//     switch (required_date) {
//         case 'registrationDate': {
//             if (!accred.expiryDate) {
//                 return !accred.registrationDate || !renewalYears ? true : isDocExpired(moment(accred.registrationDate, "DD-MM-YYYY").add(renewalYears, 'years').format("DD-MM-YYYY"))
//             }
//             return isDocExpired(accred.expiryDate)
//             //  !accred.expiryDate ? true : moment(accred.expiryDate, "DD-MM-YYYY").isBefore(moment())
//         }
//         case null: return false;
//         case 'expiryDate': default: {
//             return isDocExpired(accred.expiryDate)
//             // !accred.expiryDate ? true : moment(accred.expiryDate, "DD-MM-YYYY").isBefore(moment())
//         }
//     }
// }

// export const getSchoolState = (fields: SchoolInformation) => {
//     const match = Object.entries(fields).filter(el => {
//         if(el[0] === "employmentDeclarations") {
//             return null;
//         }
//         if(el[1] && el[1].state && el[1].state === 'rejected') {
//             return true;
//         }
//         return false;
//     })[0]
//     if(match) {
//         return 'rejected'
//     }

//     // At this point it is not rejected
//     // if there is any incomplete, then it is incomplete
//     // Then if there are any submitted, then submitted,
//     // Finally it should be approved

//     const isIncomplete = Object.entries(fields).filter(([key, value]) => {
//         if(key === "employmentDeclarations" || key === "preferredName") {
//             return null;
//         }
//         if(value && value.state && value.state === 'incomplete') {
//             return true;
//         }
//         return false;
//     })[0]
//     if(isIncomplete) {
//         return 'incomplete'
//     }

//     // If there are any fields that are missing,

//     const isSubmitted = Object.entries(fields).filter(([key, value]) => {
//         if(key === "employmentDeclarations" || key === "preferredName") {
//             return null;
//         }
//         if(value && value.state && value.state === 'submitted') {
//             return true;
//         }
//         return false;
//     })[0]
//     if(isSubmitted) {
//         return 'submitted'
//     }

//     const isApproved = Object.entries(fields).filter(([key, value]) => {
//         if(key === "employmentDeclarations" || key === "preferredName") {
//             return null;
//         }
//         if(value && value.state && value.state === 'approved') {
//             return true;
//         }
//         return false;
//     })[0]
//     if(isApproved) {
//         return 'approved'
//     }

//     return 'incomplete'
// }
